import { fetchGet, fetchPost } from "..";
import { APIResponse } from "../../types/responses/APIResponse";
import { User } from "../../types/user";
import { getDeviceId } from "../../utils/getDeviceId";

export type AccessDevice = {
  access_device: {
    id: number;
    user_email: string | null;
    identifier: string;
    active: {
      state: "on_until";
      datetime: Date;
    };
    device_name: string | null;
    device_type: string | null;
    created_at?: Date; // present on checkAppleSubscription but not createAppleSubscription
  };
  message?: string; // present on createAppleSubscription but not checkAppleSubscription
};

export type CreateAppleSubscriptionSuccess = APIResponse<AccessDevice>;
export type CreateAppleSubscriptionFailure = APIResponse<{
  error: { message: string };
}>;

export const createAppleSubscription = async (
  user: User
): Promise<APIResponse<AccessDevice>> => {
  const deviceId = await getDeviceId();

  try {
    return fetchPost(
      "streaming-access-devices",
      {},
      {
        device: {
          user_email: user?.detail?.email || "",
          identifier: deviceId,
          device_name: "",
          device_type: "",
        },
      }
    );
  } catch (e) {
    // returns obj of following type

    // {"meta":{"status":200,"code":"OK"},"access_device":{"id":3,"user_email":null,"identifier":"3ck7Ck+CMj3vm2nHoliiI4DM1LGCEhnULzldCqakGd01","active":{"state":"on_until","datetime":"2024-07-04T23:59:59+01:00"},"device_name":null,"device_type":null},"message":"Device added successfully."}

    // can also return:
    // (1) already been used, may change after 24 hrs

    // curl -v -X POST "https://api.new-staging.racingtv.com/streaming-access-devices" \
    // -H 'accept: */*' \
    // -H 'API-KEY: e83f4431-61a4-46aa-aac4-5efcbdfe9441' \
    // -H 'Content-Type: application/json' \
    // -d '{ "device": { "user_email": "", "identifier": "3ck7Ck\+CMj3vm2nHoliiI4DM1LGCEhnULzldCqakGd0122", "device_name": "", "device_type": "" } }'

    // {"meta":{"status":400,"code":"BAD_REQUEST"},"error":{"message":"Identifier has already been taken"}}%

    // (2) 500 response with a html response.

    // curl -v -X POST "https://api.new-staging.racingtv.com/streaming-access-devices" \
    // -H 'accept: */*' \
    // -H 'API-KEY: e83f4431-61a4-46aa-aac4-5efcbdfe9441' \
    // -H 'Content-Type: application/json' \
    // -d '{ "device": { "user_email": "", "identifier": "", "device_name": "", "device_type": "" } }'

    // <!DOCTYPE html><html><head><meta charset="UTF-8" /><meta content="text/html; charset=utf-8" http-equiv="Content-Type" /><meta content="width=device-width, initial-scale=1.0, viewport-fit=cover" name="viewport" /><meta content="no-cache" name="turbo-cache-control" /><meta name="csrf-param" content="authenticity_token" /><meta name="csrf-token" content="cIabxX1PwDZeP7Z3pgJUGBHaf1ZpUGYv0BPyKtN45nfViKuomnreQ7km8XsmStG1jmkhF3CCE5zc44qz0SPp-g" /><meta name="spritemap-onspace" content="/assets/onspace-20e1ca170b4f283b2e7f475ec5c8083c5532b448.svg"><meta rel="icon" href="/assets/onspace/favicon-5a27f94c546769137a2c943773718ecfeefcd32f.ico" sizes="any"><meta rel="icon" href="/assets/onspace/favicon-32-185b173bf18e496b31d1da85d61dc1f5be836a6e.png" type="image/png"><meta rel="icon" href="/assets/onspace/favicon-3c5560ef7ab502290925e08b0d3f7c88f65a9655.svg" type="image/svg+xml"><link rel="apple-touch-icon" href="/assets/onspace/appicon-180-d08690dac61cdc032239f7baa49ed7d9193578c0.png"><link rel="manifest" href="/manifest"><meta name="onspace-i18n-locale" content="en"><meta name="onspace-media-upload-url" content="/onspace/media/blobs"><title>Onspace</title><script src="/assets/onspace/application-365baeeb7ef25c844a5bb83dd7e9be2bcf7236e8.js" data-turbo-track="reload"></script><link rel="stylesheet" href="/assets/onspace/application-29e3fb3b80efdfd0605d790408393f5311d573cd.css" data-turbo-track="reload" /></head><body><nav class="onspace-navbar"><div class="onspace-navbar__content"><a class="onspace-navbar__button onspace-navbar__button--square onspace-navbar__button--mobile"></a><a class="onspace-navbar__button onspace-navbar__button--title" href="/"><svg viewBox="0 0 76 20"><use xlink:href="/assets/onspace-20e1ca170b4f283b2e7f475ec5c8083c5532b448.svg#racingtv/logo_text" /></svg></a><a class="onspace-navbar__button onspace-navbar__button--square onspace-navbar__button--mobile"></a></div></nav><main class="onspace-structure"><div class="onspace-structure__content"><div class="onspace-heading"><h1>Error</h1></div><p>An unexpected error occurred.</p></div></main></body></html>

    console.log("ERROR CREATING NEW APPLE SUBSCRIPTION: ", e);
  }
};

export type AppleSubscriptionValid = APIResponse<AccessDevice>;
export type AppleSubscriptionInvalid = APIResponse<{
  error: { title: string; text: string };
}>;

// example of device registered
// curl -v "https://api.new-staging.racingtv.com/streaming-access-devices/3ck7Ck+CMj3vm2nHoliiI4DM1LGCEhnULzldCqakhd0=" -H "accept: application/json" -H "API-KEY: e83f4431-61a4-46aa-aac4-5efcbdfe9441" -H "DEVICE-IDENTIFIER: 3ck7Ck+CMj3vm2nHoliiI4DM1LGCEhnULzldCqakhd0="
// note - this is a slightly different object to AccessDevice, since it also has a created_at key.
// {"meta":{"status":200,"code":"OK"},"access_device":{"id":2,"user_email":null,"identifier":"3ck7Ck+CMj3vm2nHoliiI4DM1LGCEhnULzldCqakhd0=","active":{"state":"on_until","datetime":"2024-07-04T23:59:00+01:00"},"device_name":null,"device_type":null,"created_at":"2024-07-04T11:33:41+01:00"}}

// example of no device registered
// curl -v "https://api.new-staging.racingtv.com/streaming-access-devices/3ck7Ck+CMj3vm2nHoliiI4DM1LGCEhnULzldCqakhd0=2" -H "accept: application/json" -H "API-KEY: e83f4431-61a4-46aa-aac4-5efcbdfe9441" -H "DEVICE-IDENTIFIER: 3ck7Ck+CMj3vm2nHoliiI4DM1LGCEhnULzldCqakhd0=2"

// {"meta":{"status":404,"code":"NOT_FOUND"},"error":{"title":"Not Found","text":"The resource could not be found."}}
export const checkAppleSubscription = async (timeNow = Date.now()) => {
  const deviceId = await getDeviceId();
  console.log(":::::::::::deviceId: ", deviceId);
  try {
    const res = await fetchGet(
      `streaming-access-devices/${deviceId}`,
      {},
      false,
      {
        "DEVICE-IDENTIFIER": deviceId,
      }
    );

    console.log(`******** appleSubCheck at ${timeNow} =>>> `, res);
    return res;
  } catch (e) {
    console.log("error checking apple subscription: ", e);
  }
};

// we should checkAppleSubscription immediately after createAppleSubscription so that
// we know the user can then watch the stream.
